import styled from 'styled-components';
import type { KsPackageAct, KsPackageMaterialSummary } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Select } from '@shared/ui/Input';
import {
  fmtMoney,
  fmtNum,
  materialDestinationHint,
  materialKindLabel,
  supplyLabel,
} from '@shared/lib/smetaLabels';

const TableTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing(5)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const FilterSelect = styled(Select)`
  max-width: 220px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const NoMaterialsMuted = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Tile = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const TileValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  margin: ${({ theme }) => theme.spacing(1)} 0;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    white-space: nowrap;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const ThRight = styled.th`
  text-align: right !important;
`;

function fmtInt(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('ru-RU');
}

function MaterialTile({ row }: { row: KsPackageMaterialSummary }) {
  return (
    <Tile>
      <Muted>
        {supplyLabel(row.supply)} · {materialKindLabel(row.kind)}
      </Muted>
      <TileValue>{fmtMoney(row.total_cost)} ₽</TileValue>
      <Muted>позиций: {fmtInt(row.count)}</Muted>
      <Hint>{materialDestinationHint(row.supply, row.kind)}</Hint>
    </Tile>
  );
}

export interface KsPackageMaterialsTabProps {
  materials: {
    id: string;
    name: string;
    name_by_doc?: string | null;
    nomenclature_no?: string | null;
    unit?: string | null;
    qty_norm?: number | null;
    qty_fact?: number | null;
    qty?: number | null;
    unit_price?: number | null;
    total_cost?: number | null;
    supply?: string | null;
    kind?: string | null;
  }[];
  materialSummary: KsPackageMaterialSummary[];
  acts: KsPackageAct[];
  actFilter: string;
  onActFilterChange: (id: string) => void;
}

export function KsPackageMaterialsTab({
  materials,
  materialSummary,
  acts,
  actFilter,
  onActFilterChange,
}: KsPackageMaterialsTabProps) {
  return (
    <>
      <TableTitle>
        Материалы строк <Badge $tone="neutral">{materials.length}</Badge>
        {acts.length > 1 && (
          <FilterSelect
            value={actFilter}
            onChange={(e) => onActFilterChange(e.target.value)}
          >
            <option value="">все акты</option>
            {acts.map((a) => (
              <option key={a.id} value={a.id}>
                акт № {a.act_no}
              </option>
            ))}
          </FilterSelect>
        )}
      </TableTitle>
      <Hint>
        Принадлежность решает, в какую форму попадёт материал и войдёт ли он в итог
        акта. Перепутанная принадлежность — это счёт заказчику за его же материал, и в
        подписанной ведомости это уже не починить.
      </Hint>
      {materialSummary.length > 0 && (
        <Tiles>
          {materialSummary.map((s) => (
            <MaterialTile key={`${s.supply}-${s.kind}`} row={s} />
          ))}
        </Tiles>
      )}
      {materials.length === 0 ? (
        <NoMaterialsMuted>
          Материалов нет. Они появляются из первички (шаг «Сборка») и из норм расхода
          при сборке пакета.
        </NoMaterialsMuted>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Наименование</th>
                <th>Ном. №</th>
                <th>Ед.</th>
                <ThRight>Норма</ThRight>
                <ThRight>Факт</ThRight>
                <ThRight>К списанию</ThRight>
                <ThRight>Цена</ThRight>
                <ThRight>Стоимость</ThRight>
                <th>Принадлежность</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m) => (
                <tr key={m.id}>
                  <td>
                    {m.name}
                    {m.name_by_doc && m.name_by_doc !== m.name && (
                      <Muted>по накладной: {m.name_by_doc}</Muted>
                    )}
                  </td>
                  <td>{m.nomenclature_no ?? '—'}</td>
                  <td>{m.unit ?? '—'}</td>
                  <td className="num">{fmtNum(m.qty_norm)}</td>
                  <td className="num">{fmtNum(m.qty_fact)}</td>
                  <td className="num">{fmtNum(m.qty)}</td>
                  <td className="num">{fmtMoney(m.unit_price)}</td>
                  <td className="num">{fmtMoney(m.total_cost)}</td>
                  <td>
                    <Badge $tone={m.supply === 'customer' ? 'info' : 'neutral'}>
                      {supplyLabel(m.supply ?? '')}
                    </Badge>{' '}
                    <Muted>{materialKindLabel(m.kind ?? '')}</Muted>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </>
  );
}
